import { Cubit } from "blac-next";
import parseJwt from "src/lib/parseJwt";
import envVariables from "src/lib/envVariables";

/**
 * Lives in localStorage next to the member's tokens: a per-tab flag would let a
 * reopened tab restore the assumed session with writes enabled.
 */
export const ASSUMED_IDENTITY_STORAGE_KEY = "9am.assumed-identity";

const READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const MAX_LISTED_BLOCKED_REQUESTS = 100;

export interface AssumedIdentity {
  userId: string;
  expiresAt: string;
}

export interface BlockedRequest {
  method: string;
  path: string;
}

export interface AssumedIdentityState {
  identity?: AssumedIdentity;
  writeMode: boolean;
  blockedCount: number;
  /** Newest first, capped; `blockedCount` keeps the full total. */
  blockedRequests: BlockedRequest[];
}

export type ParsedAssumedTokens =
  | { ok: true; identity: AssumedIdentity }
  | { ok: false; reason: string };

type TokenType = "ACCESS" | "REFRESH";

const parseToken = (token: string, type: TokenType) => {
  const label = `${type.toLowerCase()} token`;
  try {
    const jwt = parseJwt(token.trim());
    if (!jwt.sub) {
      return { reason: `Not a valid ${label}` };
    }
    if (jwt.type && jwt.type !== type) {
      return { reason: `Expected an ${type} token, got ${jwt.type}` };
    }
    if (jwt.expired) {
      return { reason: `The ${label} has expired` };
    }
    return { jwt, userId: jwt.sub };
  } catch {
    return { reason: `Not a valid ${label}` };
  }
};

/**
 * Both tokens are required: a session is only restored from a stored refresh
 * token, so an access token alone would fall back to logged-out on reload.
 */
export const parseAssumedTokens = ({
  accessToken,
  refreshToken
}: {
  accessToken: string;
  refreshToken: string;
}): ParsedAssumedTokens => {
  const access = parseToken(accessToken, "ACCESS");
  if (!access.jwt) {
    return { ok: false, reason: access.reason };
  }
  const refresh = parseToken(refreshToken, "REFRESH");
  if (!refresh.jwt) {
    return { ok: false, reason: refresh.reason };
  }
  if (refresh.userId !== access.userId) {
    return { ok: false, reason: "The tokens belong to different users" };
  }
  return {
    ok: true,
    identity: {
      userId: access.userId,
      expiresAt: access.jwt.expiresAt.toISOString()
    }
  };
};

interface StoredFlag extends AssumedIdentity {
  /** Set once the member's tokens are stored, so other tabs reload into a usable session. */
  sessionReady?: boolean;
}

/** A present but unreadable flag still counts as active, so the guard fails closed. */
const readStoredFlag = (): StoredFlag | undefined => {
  const raw = localStorage.getItem(ASSUMED_IDENTITY_STORAGE_KEY);
  if (raw === null) {
    return undefined;
  }
  try {
    const stored = JSON.parse(raw) as Partial<StoredFlag>;
    return {
      userId: stored.userId ?? "unknown",
      expiresAt: stored.expiresAt ?? "",
      sessionReady: stored.sessionReady
    };
  } catch {
    return { userId: "unknown", expiresAt: "" };
  }
};

const readStoredIdentity = (): AssumedIdentity | undefined => {
  const flag = readStoredFlag();
  return flag && { userId: flag.userId, expiresAt: flag.expiresAt };
};

const writeStoredFlag = (flag: StoredFlag): void => {
  localStorage.setItem(ASSUMED_IDENTITY_STORAGE_KEY, JSON.stringify(flag));
};

/**
 * Staff session running on a member's pasted tokens. Write mode is never
 * persisted, so every page load starts read-only.
 */
export default class AssumedIdentityCubit extends Cubit<AssumedIdentityState> {
  static keepAlive = true;

  constructor() {
    super({
      identity: readStoredIdentity(),
      writeMode: false,
      blockedCount: 0,
      blockedRequests: []
    });
    window.addEventListener("storage", this.handleStorageChange);
  }

  /**
   * Other tabs keep their previous tokens in memory, so they reload when this
   * session is exited elsewhere or replaced by another member's.
   */
  private readonly handleStorageChange = (event: StorageEvent): void => {
    const flagChanged =
      event.key === null || event.key === ASSUMED_IDENTITY_STORAGE_KEY;
    if (!flagChanged) {
      return;
    }
    const stored = readStoredFlag();
    const exitedElsewhere = this.isActive && stored === undefined;
    const replacedElsewhere =
      stored?.sessionReady === true &&
      stored.userId !== this.state.identity?.userId;
    if (exitedElsewhere || replacedElsewhere) {
      window.location.assign(envVariables.BASE_PATH);
    }
  };

  get isActive(): boolean {
    return this.state.identity !== undefined;
  }

  begin = (identity: AssumedIdentity): void => {
    writeStoredFlag(identity);
    this.emit({
      identity,
      writeMode: false,
      blockedCount: 0,
      blockedRequests: []
    });
  };

  markSessionReady = (): void => {
    const { identity } = this.state;
    if (identity) {
      writeStoredFlag({ ...identity, sessionReady: true });
    }
  };

  end = (): void => {
    localStorage.removeItem(ASSUMED_IDENTITY_STORAGE_KEY);
    this.emit({ writeMode: false, blockedCount: 0, blockedRequests: [] });
  };

  setWriteMode = (writeMode: boolean): void => {
    if (!this.isActive) {
      return;
    }
    this.emit({ ...this.state, writeMode });
  };

  allowsRequest = (method: string): boolean =>
    !this.isActive ||
    this.state.writeMode ||
    READ_METHODS.has(method.toUpperCase());

  recordBlocked = (method: string, path: string): void => {
    const blocked = { method: method.toUpperCase(), path };
    this.emit({
      ...this.state,
      blockedCount: this.state.blockedCount + 1,
      blockedRequests: [blocked, ...this.state.blockedRequests].slice(
        0,
        MAX_LISTED_BLOCKED_REQUESTS
      )
    });
  };
}
